package org.catharsis.spendtel.core.impl

import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.firestore.FirebaseFirestore
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import org.catharsis.spendtel.core.api.SpendTelRepo
import org.catharsis.spendtel.feature.data.SpendtelUser
import org.catharsis.spendtel.feature.data.toMap

import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class SpendTelRepoImpl(
    private val firebaseFirestore: FirebaseFirestore,
    private val firebaseAuth: FirebaseAuth,
): SpendTelRepo {

    override fun listenToUserChanges(uid: String): Flow<SpendtelUser?> {
        val userCollection = firebaseFirestore.collection("USERS").document(uid)
        return userCollection.snapshots.map { snapshot ->
            if (!snapshot.exists) {
                Napier.d("listenToUserChanges -> no document for uid=$uid")
                return@map null
            }

            try {
                val user = snapshot.data<SpendtelUser>()
                Napier.d("listenToUserChanges -> $user")
                user
            } catch (e: Exception) {
                Napier.e("listenToUserChanges decode failed for uid=$uid: ${e.message}")
                null
            }
        }.catchFirestorePermissionDenied("listenToUserChanges(uid=$uid)") { null }
    }

    override suspend fun getUserById(uid: String): SpendtelUser? {
        return try {
            val userResponse = firebaseFirestore.collection("USERS").document(uid).get()
            if (!userResponse.exists) return null
            userResponse.data<SpendtelUser>()
        } catch (e: Exception) {
            Napier.e(e.message ?: "An error occurred fetching user with id $uid")
            null
        }
    }

    override suspend fun getTotalNumberOfUsers(): Result<Int> {
        return try {
            val response = firebaseFirestore.collection("USERS").get().documents.size

            Result.success(response)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error fetching getTotalNumberOfUsers"))
        }
    }

    override suspend fun updateUsername(userId: String, name: String): Result<Unit> {
        return try {
            firebaseFirestore.collection("USERS").document(userId).update(mapOf("username" to name))

            return Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error occurred updating username"))
        }
    }

    override suspend fun updateUserGeneralNotificationsSetting(
        userId: String,
        enable: Boolean
    ): Result<Unit> {
        return try {
            firebaseFirestore.collection("USERS").document(userId).update(mapOf("generalNotificationsAllowed" to enable))
            return Result.success(Unit)
        } catch (e: Exception) {
          Result.failure(Throwable(e.message ?: "Error occurred updating UserGeneralNotificationsSetting"))
        }
    }

    override suspend fun updateUserDarkModeSetting(
        userId: String,
        darkModeActive: Boolean
    ): Result<Unit> {
        return try {
            firebaseFirestore.collection("USERS").document(userId).update(mapOf("darkModeActive" to darkModeActive))
            return Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error occurred updating users darkModeActive setting"))
        }
    }

    override suspend fun updateUserFCMToken(userId: String, token: String): Result<Unit> {
        return try {
            firebaseFirestore.collection("USERS").document(userId).update(mapOf("fcmToken" to token))
            return Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error occurred updating user fcmToken"))
        }
    }

    override suspend fun updateUserEULAAgreement(userId: String, agreed: Boolean): Result<Unit> {
        return try {
            firebaseFirestore.collection("USERS").document(userId).update(mapOf("agreedToEULA" to agreed))
            return Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error occurred updating user agreedToEULA status"))
        }
    }

    override suspend fun updateFinancialGoal(user: SpendtelUser, goalId: String): Result<Unit> {
        return try {
            val updatedAt = Clock.System.now().toEpochMilliseconds()
            val updatedUser = user.copy(
                financialGoal = goalId,
                financialGoalUpdatedAt = updatedAt,
            )
            val document = firebaseFirestore.collection("USERS").document(user.uid)
            if (document.get().exists) {
                document.update(
                    mapOf(
                        "financialGoal" to goalId,
                        "financialGoalUpdatedAt" to updatedAt.toDouble(),
                    ),
                )
            } else {
                persistSpendtelUserProfile(firebaseFirestore, firebaseAuth, updatedUser)
                    .getOrElse { error ->
                        val message = error.message ?: "Could not create your profile. Please try again."
                        return Result.failure(Throwable(message))
                    }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(Throwable(e.message ?: "Error occurred updating financial goal"))
        }
    }

    override suspend fun updateUser(user: SpendtelUser) {
        firebaseFirestore.collection("USERS").document(user.uid).update(user.toMap())
    }

    override suspend fun deleteUser(uid: String) {
        firebaseFirestore.collection("USERS").document(uid).delete()
    }

    // endregion
}